import axios, { AxiosError } from 'axios'
import { io, Socket } from 'socket.io-client'

// Create axios instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now(),
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // Handle 401 - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        // Dynamic import to avoid circular dependency
        const { useAuthStore } = await import('@/stores/auth')
        const authStore = useAuthStore()
        
        const refreshed = await authStore.refreshToken()
        if (refreshed) {
          return api(originalRequest)
        }
      } catch {
        // Refresh failed, redirect to login
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// WebSocket connection
class WebSocketConnection {
  private socket: Socket | null = null
  private listeners: Map<string, Set<Function>> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5

  connect() {
    const token = localStorage.getItem('xdns_token')
    
    this.socket = io(import.meta.env.VITE_WS_URL || window.location.origin, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    })

    this.socket.on('connect', () => {
      console.log('[WS] Connected')
      this.reconnectAttempts = 0
    })

    this.socket.on('disconnect', (reason) => {
      console.log('[WS] Disconnected:', reason)
    })

    this.socket.on('error', (error) => {
      console.error('[WS] Error:', error)
    })

    // Re-register all listeners
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket?.on(event, callback as any)
      })
    })
  }

  disconnect() {
    this.socket?.disconnect()
    this.socket = null
  }

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)
    
    if (this.socket) {
      this.socket.on(event, callback as any)
    }
  }

  off(event: string, callback?: Function) {
    if (callback) {
      this.listeners.get(event)?.delete(callback)
      this.socket?.off(event, callback as any)
    } else {
      this.listeners.delete(event)
      this.socket?.off(event)
    }
  }

  emit(event: string, data?: any) {
    this.socket?.emit(event, data)
  }

  get isConnected(): boolean {
    return this.socket?.connected ?? false
  }
}

export const wsConnection = new WebSocketConnection()

// API endpoints grouped by domain
export const endpoints = {
  // Auth
  auth: {
    login: () => '/api/v1/auth/login',
    logout: () => '/api/v1/auth/logout',
    refresh: () => '/api/v1/auth/refresh',
    me: () => '/api/v1/auth/me',
  },
  
  // Dashboard
  dashboard: {
    metrics: () => '/api/v1/dashboard/metrics',
    queryStats: (range: string) => `/api/v1/dashboard/query-stats?range=${range}`,
    topQueries: (limit?: number) => `/api/v1/dashboard/top-queries${limit ? `?limit=${limit}` : ''}`,
    topClients: (limit?: number) => `/api/v1/dashboard/top-clients${limit ? `?limit=${limit}` : ''}`,
  },
  
  // Zones
  zones: {
    list: () => '/api/v1/zones',
    get: (id: string) => `/api/v1/zones/${id}`,
    create: () => '/api/v1/zones',
    update: (id: string) => `/api/v1/zones/${id}`,
    delete: (id: string) => `/api/v1/zones/${id}`,
    sync: (id: string) => `/api/v1/zones/${id}/sync`,
    export: (id: string) => `/api/v1/zones/${id}/export`,
    import: () => '/api/v1/zones/import',
  },
  
  // Records
  records: {
    list: (zoneId?: string) => zoneId ? `/api/v1/zones/${zoneId}/records` : '/api/v1/records',
    get: (zoneId: string, id: string) => `/api/v1/zones/${zoneId}/records/${id}`,
    create: (zoneId: string) => `/api/v1/zones/${zoneId}/records`,
    update: (zoneId: string, id: string) => `/api/v1/zones/${zoneId}/records/${id}`,
    delete: (zoneId: string, id: string) => `/api/v1/zones/${zoneId}/records/${id}`,
    bulk: (zoneId: string) => `/api/v1/zones/${zoneId}/records/bulk`,
  },
  
  // Servers
  servers: {
    list: () => '/api/v1/servers',
    get: (id: string) => `/api/v1/servers/${id}`,
    health: (id: string) => `/api/v1/servers/${id}/health`,
    metrics: (id: string) => `/api/v1/servers/${id}/metrics`,
  },
  
  // Analytics
  analytics: {
    queries: (params?: Record<string, any>) => {
      const query = new URLSearchParams(params).toString()
      return `/api/v1/analytics/queries${query ? `?${query}` : ''}`
    },
    geoip: (ip: string) => `/api/v1/analytics/geoip/${ip}`,
    topDomains: (range?: string) => `/api/v1/analytics/top-domains${range ? `?range=${range}` : ''}`,
    topClients: (range?: string) => `/api/v1/analytics/top-clients${range ? `?range=${range}` : ''}`,
    anomalies: () => '/api/v1/analytics/anomalies',
  },
  
  // Audit
  audit: {
    list: (params?: Record<string, any>) => {
      const query = new URLSearchParams(params).toString()
      return `/api/v1/audit${query ? `?${query}` : ''}`
    },
    export: (format: 'json' | 'csv') => `/api/v1/audit/export?format=${format}`,
  },
  
  // Users
  users: {
    list: () => '/api/v1/users',
    get: (id: string) => `/api/v1/users/${id}`,
    create: () => '/api/v1/users',
    update: (id: string) => `/api/v1/users/${id}`,
    delete: (id: string) => `/api/v1/users/${id}`,
  },
  
  // API Keys
  apiKeys: {
    list: () => '/api/v1/api-keys',
    create: () => '/api/v1/api-keys',
    revoke: (id: string) => `/api/v1/api-keys/${id}`,
  },
}
